import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import {
  cmdbApi, itsmApi, organizationApi,
  type ConfigItem, type ItsmCollection,
} from '../../services/api';
import { RaisePriorityFields } from './PrioritySla';
import {
  EMPTY_RAISE_PRIORITY, raisePriorityPayload, useSlaPolicy, type RaisePriorityValue,
} from './itsmSla';
import {
  errorText, field, help, label, primaryButton, ring, secondaryButton, useAssigneeOptions,
  warnText,
} from './itsmUi';

interface Org { _id: string; name: string; externalOrgId?: string; researchOrgId?: string }

/** The keys a CI's CRM `orgId` may hold for this organisation (crm_org_sync.find_organization). */
function orgKeys(org: Org | undefined): Set<string> {
  return new Set([org?._id, org?.externalOrgId, org?.researchOrgId].filter(Boolean) as string[]);
}

/**
 * Raise a ticket against an ORGANISATION and a SERVICE (PO46-1272).
 *
 * No project field: the server files the ticket under the organisation's
 * Operations project, and the service decides the owner group it routes to.
 * Leaving the service empty is allowed, and the ticket is then flagged as
 * having no service to route to.
 *
 * A Headless UI Dialog (design standard: dialog): focus is trapped, Escape
 * closes and focus returns to the Raise button.
 */
export default function RaiseTicketDialog({ collection, defaultOrgId, knownAssignees, onClose, onRaised }: {
  collection: ItsmCollection;
  defaultOrgId?: string;
  knownAssignees?: string[];
  onClose: () => void;
  onRaised: () => void;
}) {
  const [orgs, setOrgs] = useState<Org[] | null>(null);
  const [services, setServices] = useState<ConfigItem[] | null>(null);
  const [orgId, setOrgId] = useState(defaultOrgId || '');
  const [ciId, setCiId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignee, setAssignee] = useState('');
  const [major, setMajor] = useState(false);
  const [prio, setPrio] = useState<RaisePriorityValue>(EMPTY_RAISE_PRIORITY);
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const pol = useSlaPolicy();
  const people = useAssigneeOptions(knownAssignees);

  useEffect(() => {
    organizationApi.list().then((o) => setOrgs(o || [])).catch(() => setOrgs([]));
    cmdbApi.list().then((d) => setServices(d.items || [])).catch(() => setServices([]));
  }, []);

  // One organisation visible: it is the answer, so it is chosen for the person.
  useEffect(() => {
    if (!orgId && orgs && orgs.length === 1) setOrgId(orgs[0]._id);
  }, [orgs, orgId]);

  const org = useMemo(() => (orgs || []).find((o) => o._id === orgId), [orgs, orgId]);
  // Only the chosen organisation's services: one from another would be refused,
  // because it would file this outage in a second client's container.
  const orgServices = useMemo(() => {
    const keys = orgKeys(org);
    return (services || []).filter((ci) => ci.orgId && keys.has(ci.orgId));
  }, [services, org]);

  useEffect(() => { setCiId(''); }, [orgId]);

  const titleMissing = !title.trim();
  const orgMissing = !orgId;
  const noun = collection.slice(0, -1);

  const save = async (e?: FormEvent) => {
    e?.preventDefault();
    setTried(true);
    if (titleMissing || orgMissing) return;          // announced inline on the fields
    const priority = raisePriorityPayload(pol, prio);
    if (typeof priority === 'string') return;
    setSaving(true);
    try {
      await itsmApi.create(collection, {
        title: title.trim(),
        orgId,
        affectedCIs: ciId ? [ciId] : [],
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(assignee.trim() ? { assignee: assignee.trim() } : {}),
        ...priority,
        ...(collection === 'incidents' ? { majorIncident: major } : {}),
      });
      toast.success(ciId ? 'Raised' : 'Raised. It has no service yet, so it will not route to a queue.');
      onRaised();
    } catch (err: unknown) {
      toast.error((err instanceof Error && err.message) || 'Could not raise it. Try again.',
        { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-slate-950/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-lg rounded-xl border border-slate-200 dark:border-slate-800 bg-panel dark:bg-slate-900 shadow-xl max-h-[90vh] flex flex-col">
          <form onSubmit={save} noValidate className="flex flex-col min-h-0">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
              <DialogTitle className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Raise {noun}
              </DialogTitle>
              <button type="button" onClick={onClose} aria-label="Close without raising"
                className={`text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 rounded ${ring}`}>
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div>
                <label htmlFor="raise-title" className={label}>What happened</label>
                <input id="raise-title" className={field} value={title} autoFocus required
                  aria-invalid={tried && titleMissing ? true : undefined}
                  aria-describedby={tried && titleMissing ? 'raise-title-error' : undefined}
                  onChange={(e) => setTitle(e.target.value)} />
                {tried && titleMissing && (
                  <p id="raise-title-error" className={errorText}>Say what happened in a few words.</p>
                )}
              </div>

              <div>
                <label htmlFor="raise-org" className={label}>Organisation</label>
                <select id="raise-org" className={field} value={orgId} required
                  disabled={orgs === null}
                  aria-invalid={tried && orgMissing ? true : undefined}
                  aria-describedby={`raise-org-help${tried && orgMissing ? ' raise-org-error' : ''}`}
                  onChange={(e) => setOrgId(e.target.value)}>
                  <option value="">{orgs === null ? 'Loading…' : 'Choose an organisation'}</option>
                  {(orgs || []).map((o) => (
                    <option key={o._id} value={o._id}>{o.name}</option>
                  ))}
                </select>
                <p id="raise-org-help" className={help}>
                  Whose service is affected. The ticket is filed in that organisation's Operations project.
                </p>
                {tried && orgMissing && (
                  <p id="raise-org-error" className={errorText}>Choose the organisation this is for.</p>
                )}
              </div>

              <div>
                <label htmlFor="raise-ci" className={label}>Affected service</label>
                <select id="raise-ci" className={field} value={ciId}
                  disabled={!orgId || services === null}
                  aria-describedby="raise-ci-help"
                  onChange={(e) => setCiId(e.target.value)}>
                  <option value="">
                    {services === null ? 'Loading…' : orgId ? 'Not known yet' : 'Choose an organisation first'}
                  </option>
                  {orgServices.map((ci) => (
                    <option key={ci._id} value={ci._id}>
                      {ci.name} · {ci.ciType}{ci.ownerGroup ? ` · ${ci.ownerGroup}` : ''}
                    </option>
                  ))}
                </select>
                <p id="raise-ci-help" className={orgId && !ciId ? `mt-1 ${warnText}` : help}>
                  {orgId && !ciId
                    ? (orgServices.length === 0 && services !== null
                      ? 'This organisation has no services recorded. The ticket will be flagged as having no service to route to.'
                      : 'Without a service the ticket is flagged as having no service, and no queue owns it.')
                    : 'The service decides which queue owns the ticket.'}
                </p>
              </div>

              <div>
                <label htmlFor="raise-description" className={label}>Description</label>
                <textarea id="raise-description" className={field} rows={3} value={description}
                  aria-describedby="raise-description-help"
                  onChange={(e) => setDescription(e.target.value)} />
                <p id="raise-description-help" className={help}>
                  What you saw, when it started, and who is affected.
                </p>
              </div>

              <RaisePriorityFields value={prio} showErrors={tried}
                onChange={(patch) => setPrio((v) => ({ ...v, ...patch }))} />

              <div>
                <label htmlFor="raise-assignee" className={label}>Assignee</label>
                <input id="raise-assignee" type="email" className={field} value={assignee}
                  list="raise-assignee-options" autoComplete="off"
                  aria-describedby="raise-assignee-help"
                  onChange={(e) => setAssignee(e.target.value)} />
                <datalist id="raise-assignee-options">
                  {people.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}
                </datalist>
                <p id="raise-assignee-help" className={help}>
                  Optional. Their PMO email; they are told by email.
                </p>
              </div>

              {collection === 'incidents' && (
                <div className="flex items-center gap-2">
                  <input id="raise-major" type="checkbox" checked={major}
                    className={`h-4 w-4 rounded border-slate-400 accent-accent ${ring}`}
                    onChange={(e) => setMajor(e.target.checked)} />
                  <label htmlFor="raise-major" className="text-sm text-slate-700 dark:text-slate-200">
                    Major incident
                  </label>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-800">
              <button type="button" onClick={onClose} className={secondaryButton}>Cancel</button>
              <button type="submit" disabled={saving} className={primaryButton}>
                {saving ? 'Raising…' : `Raise ${noun}`}
              </button>
            </div>
          </form>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
